import { Fragment } from 'react';
import TableRow from './TableRow';
import {
  DataTableRow,
  DataTableVariant,
  DataTableBaseColumn,
  DataTableSortableColumn,
  DataTableSortableProps,
} from './TableTypes';
import { ReachedLastRow } from './ReachedLastRow';

interface TableBodyRowsProps<TRow extends DataTableRow>
  extends Pick<
    DataTableSortableProps<TRow>,
    'rows' | 'columns' | 'containerRef' | 'onRowClick' | 'renderRow' | 'reachedLastRow'
  > {
  variant: DataTableVariant;
  overColumnId?: string | null;
  draggedColumnId?: string | null;
  isColumnReorderAllowed?: boolean;
  isColumnResizeAllowed?: boolean;
}

export function TableBodyRows<TRow extends DataTableRow = any>({
  rows,
  columns,
  containerRef,
  onRowClick,
  renderRow,
  reachedLastRow,
  variant,
  overColumnId,
  draggedColumnId,
  isColumnResizeAllowed,
  ...dataTableProps
}: TableBodyRowsProps<TRow>) {
  const renderSingleRow = (row: TRow, rowIdx: number, rowKey: string) => {
    const renderedRow =
      renderRow &&
      renderRow({
        rowIdx,
        row,
        rowKey,
      });

    if (renderedRow) {
      return renderedRow;
    }

    return (
      <TableRow
        key={row.id}
        row={row}
        columns={columns}
        tableRowProps={{ id: `table_row_${rowIdx}` }}
        onClick={
          !!onRowClick
            ? (
                e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
                column: DataTableBaseColumn | DataTableSortableColumn<TRow>,
              ) => {
                //verify that the click is on
                //the row (and not on a row's "actions menu popover" backdrop)
                if (e.target instanceof Element && e.target?.matches('tr *')) {
                  onRowClick(e, row, column);
                }
              }
            : undefined
        }
        variant={variant}
        overColumnId={overColumnId}
        draggedColumnId={draggedColumnId}
        isColumnResizeAllowed={isColumnResizeAllowed}
        {...dataTableProps}
      />
    );
  };

  return (
    <>
      {rows.map((row, rowIdx) => (
        <Fragment key={`wrapper_${row.id}`}>
          {renderSingleRow(row, rowIdx, `base_row_${rowIdx}`)}
          {row?.nestedRows &&
            row.nestedRows.map((row: any, idx: number) =>
              renderSingleRow(row, idx, `nested_row_${row.id}`),
            )}
        </Fragment>
      ))}
      {rows.length === reachedLastRow?.rowsMaxCount && (
        <ReachedLastRow
          columnsCount={columns.length}
          reachedLastRowTxt={reachedLastRow.txt}
          scrollToTop={() => {
            containerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
    </>
  );
}
